import {
    IconBriefcase, IconChartLine, IconHomeFilled, IconLayoutGrid, IconPlug, IconSquareCheck, IconUsers,
} from '@tabler/icons-react';
import { URLS } from 'services/urls';
import {
    ANALYTICS_BENCHMARK_SUBSECTION,
    ANALYTICS_FORECAST_SUBSECTION,
} from 'components/NSNavbar/constants';
import { sortChildrenAlpha } from '../../helpers';
import { ISideNavChildEntry, ISideNavSection, ISideNavSubItem } from '../../interfaces';

export interface IBuildPortfolioSectionsParams {
    hasCoreAppAccess: boolean;
    isPAPEnabled: boolean;
    ldFlagVendorRevampQ22026: boolean;
    hasContactsAccess: boolean;
    hasCompaniesAccess: boolean;
    isTurkUser: boolean;
}

export const buildPortfolioSections = ({
    hasCoreAppAccess,
    isPAPEnabled,
    ldFlagVendorRevampQ22026,
    hasContactsAccess,
    hasCompaniesAccess,
    isTurkUser,
}: IBuildPortfolioSectionsParams): ISideNavSection[] => {
    if (isTurkUser) {
        return [];
    }
    const benchmarkChildren: ISideNavSubItem[] = hasCoreAppAccess ? [
        {
            id: 'costAnalytics',
            label: 'Cost Analytics',
            to: URLS.PROJECTS_APP.COST_ANALYTICS,
            useAnchorTag: true,
        },
        {
            id: 'quickAnalytics',
            label: 'Quick Analytics',
            to: URLS.PROJECTS_APP.QUICK_ANALYTICS,
            useAnchorTag: true,
        },
        {
            id: 'vendorAnalytics',
            label: 'Vendor Analytics',
            to: URLS.PROJECTS_APP.VENDOR_ANALYTICS,
            useAnchorTag: true,
        },
    ] : [];

    const forecastChildren: ISideNavSubItem[] = hasCoreAppAccess && isPAPEnabled ? [
        {
            id: 'developerFee',
            label: 'Developer Fee',
            to: URLS.PROJECTS_APP.DEVELOPER_FEE,
            useAnchorTag: true,
        },
        {
            id: 'globalCostForecast',
            label: 'Global Cost Forecast',
            to: URLS.PROJECTS_APP.GLOBAL_COST_FORECAST_V2,
            useAnchorTag: true,
        },
        {
            id: 'portfolioCapitalPlanning',
            label: 'Portfolio Capital Planning',
            to: URLS.PROJECTS_APP.PORTFOLIO_CAPITAL_PLANNING,
            useAnchorTag: true,
        },
    ] : [];

    const analyticsChildren: ISideNavChildEntry[] = [];
    if (benchmarkChildren.length > 0) {
        analyticsChildren.push({
            id: 'analyticsBenchmarkHeader',
            kind: 'subgroupHeader',
            label: ANALYTICS_BENCHMARK_SUBSECTION.title,
        });
        analyticsChildren.push(...sortChildrenAlpha(benchmarkChildren));
    }
    if (forecastChildren.length > 0) {
        analyticsChildren.push({
            id: 'analyticsForecastHeader',
            kind: 'subgroupHeader',
            label: ANALYTICS_FORECAST_SUBSECTION.title,
        });
        analyticsChildren.push(...sortChildrenAlpha(forecastChildren));
    }

    const utilitiesChildren: ISideNavSubItem[] = [
        {
            id: 'budgetCategorizations',
            label: 'Budget Categorizations',
            to: URLS.PROJECTS_APP.PORTFOLIO_BUDGET_CATEGORIZATIONS,
            useAnchorTag: true,
        },
        {
            id: 'portfolioInvoices',
            label: 'Invoices',
            to: URLS.PROJECTS_APP.PORTFOLIO_INVOICES,
            useAnchorTag: true,
        },
        ...(!ldFlagVendorRevampQ22026 ? [{
            id: 'vendorDirectory',
            label: 'Vendor Directory',
            to: URLS.PROJECTS_APP.VENDOR_DIRECTORY,
            useAnchorTag: true,
        }] : []),
    ];

    const dealsChildren: ISideNavSubItem[] = [
        {
            id: 'dealManagement',
            label: 'Deal Management',
            to: URLS.DEALS.HOME,
        },
        {
            id: 'proFormas',
            label: 'Pro Formas',
            to: URLS.DEALS.PRO_FORMAS,
        },
        {
            id: 'reports',
            label: 'Reports',
            to: URLS.REPORTS.HOME,
        },
        {
            id: 'investmentMemo',
            label: 'Investment Memo',
            to: URLS.INVESTMENT_MEMO,
        },
        ...(hasContactsAccess ? [{
            id: 'contacts',
            label: 'Contacts',
            to: URLS.PROJECTS_APP.CONTACTS,
            useAnchorTag: true,
        }] : []),
        ...(hasCompaniesAccess ? [{
            id: 'companies',
            label: 'Companies',
            to: URLS.PROJECTS_APP.COMPANIES,
            useAnchorTag: true,
        }] : []),
    ];

    const portfolioSection: ISideNavSection = {
        id: 'portfolio',
        section: 'PORTFOLIO',
        items: [
            {
                id: 'feed',
                label: 'Feed',
                icon: IconHomeFilled,
                kind: 'standard',
                to: URLS.PROJECTS_APP.FEED,
                useAnchorTag: true,
            },
            ...(hasCoreAppAccess ? [{
                id: 'projects',
                label: 'Projects',
                icon: IconLayoutGrid,
                kind: 'standard' as const,
                to: URLS.PROJECTS_APP.PROJECTS,
                useAnchorTag: true,
            }] : []),
            ...(analyticsChildren.length > 0 ? [{
                id: 'analytics',
                label: 'Analytics',
                icon: IconChartLine,
                kind: 'hasChildren' as const,
                children: analyticsChildren,
            }] : []),
            ...(ldFlagVendorRevampQ22026 ? [{
                id: 'portfolioVendors',
                label: 'Portfolio Vendors',
                icon: IconUsers,
                kind: 'standard' as const,
                to: URLS.PROJECTS_APP.PORTFOLIO_VENDORS,
                useAnchorTag: true,
            }] : []),
            {
                id: 'utilities',
                label: 'Utilities',
                icon: IconPlug,
                kind: 'hasChildren',
                children: utilitiesChildren,
            },
            {
                id: 'deals',
                label: 'Deals',
                icon: IconBriefcase,
                kind: 'hasChildren',
                children: dealsChildren,
                preserveChildOrder: true,
            },
            {
                id: 'approvals',
                label: 'Approvals',
                icon: IconSquareCheck,
                kind: 'standard',
                to: URLS.PROJECTS_APP.APPROVALS,
                useAnchorTag: true,
            },
        ],
    };

    return [portfolioSection];
};
